import { legacyMegaraxHttpService } from "@legacy-megarax/crm-contracts";
import { MegaraxConnectionProvider } from "@legacy-megarax/react-client";
import { useConfig } from "@megarax/dev-config";
import posthog from "posthog-js";
import { PropsWithChildren } from "react";

import { createTestUserAuthHeader, TestUserPayload } from "@megaron/access-control";
import { authHttpService } from "@megaron/auth-contracts";
import { IamAuthContext } from "@megaron/auth-react";
import { cashRewardsHttpService } from "@megaron/cash-rewards-contracts";
import { catalogHttpService } from "@megaron/catalog-contracts";
import { commentsHttpService } from "@megaron/comments-contracts";
import { crmHttpService } from "@megaron/crm-contracts";
import { docsHttpService } from "@megaron/docs-contracts";
import { fulfillmentHttpService } from "@megaron/fulfillment-contracts";
import { HttpServiceClient } from "@megaron/http-service-client";
import { invoicesHttpService } from "@megaron/invoices-contracts";
import { pointsHttpService } from "@megaron/loyalty-points-contracts";
import { qrSyncService } from "@megaron/loyalty-qr-sync-contracts";
import { notificationsHttpService } from "@megaron/notifications-contracts";
import { shippingHttpService } from "@megaron/shipping-contracts";
import { supportHttpService } from "@megaron/support-contracts";
import { ClientContext } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

type Props = {
  auth: IamAuthContext;
} & PropsWithChildren;

export const ApiServicesProvider: React.FC<Props> = ({ children, auth }) => {
  const { config } = useConfig();

  const trackUserSignup = (userId: string | undefined | null) => {
    if (posthog && userId) {
      posthog.identify(userId);
    }
  };

  const getAuthHeader = (auth: IamAuthContext) => async () => {
    const { email, groups, phoneNumber, roles, type, uuid } = config.testUser;

    const testUserPayload: TestUserPayload | undefined = config.testUser.enabled
      ? {
          email: email || undefined,
          groups,
          phoneNumber: phoneNumber || undefined,
          roles,
          userType: type || undefined,
          uuid: uuid ? Uuid(uuid) : undefined,
        }
      : undefined;

    if (testUserPayload) {
      return createTestUserAuthHeader(testUserPayload);
    }
    trackUserSignup(auth.iamUser?.userType === "megarax" ? auth.iamUser?.email : auth.iamUser?.loyaltyId);
    return auth.getAuthHeader();
  };

  return (
    <MegaraxConnectionProvider tokenProvider={getAuthHeader(auth)}>
      <ClientContext.Provider
        value={{
          loyaltyPoints: HttpServiceClient(config.crmUrl + "/points", pointsHttpService, getAuthHeader(auth)),
          crm: HttpServiceClient(config.crmUrl + "/crm", crmHttpService, getAuthHeader(auth)),
          fulfillment: HttpServiceClient(config.crmUrl + "/fulfillment", fulfillmentHttpService, getAuthHeader(auth)),
          catalog: HttpServiceClient(config.crmUrl + "/catalog", catalogHttpService, getAuthHeader(auth)),
          qrSync: HttpServiceClient(config.qrSyncUrl, qrSyncService, getAuthHeader(auth)),
          auth: HttpServiceClient(config.accountsUrl, authHttpService, getAuthHeader(auth)),
          notifications: HttpServiceClient(
            config.accountsUrl + "/notifications",
            notificationsHttpService,
            getAuthHeader(auth),
          ),
          docs: HttpServiceClient(config.docsUrl, docsHttpService, getAuthHeader(auth)),
          invoices: HttpServiceClient(config.invoicesUrl, invoicesHttpService, getAuthHeader(auth)),
          support: HttpServiceClient(config.crmUrl, supportHttpService, getAuthHeader(auth)),
          comments: HttpServiceClient(config.crmUrl, commentsHttpService, getAuthHeader(auth)),
          legacyMegarax: HttpServiceClient(config.apiUrl, legacyMegaraxHttpService, getAuthHeader(auth)),
          cashRewards: HttpServiceClient(config.crmUrl + "/cash-rewards", cashRewardsHttpService, getAuthHeader(auth)),
          shipping: HttpServiceClient(config.crmUrl + "/shipping", shippingHttpService, getAuthHeader(auth)),
        }}
      >
        {children}
      </ClientContext.Provider>
    </MegaraxConnectionProvider>
  );
};
